import { isEqual, isEmpty, get, cloneDeep, assign, xorWith, mapValues } from 'lodash';
import { connect } from 'react-redux';

import { filterContractors } from '../../components/FindAContractor/Filter';
import { LOAD_DATA, SET_MAX_DISTANCE, SET_SORT_BY, SET_MOBILE_FILTERS_OPENED } from '../actions/FindAContractorActions';
import { SET_URL_PARAM } from '../actions/URLSearchParamActions';

const FILTER_ATTRIBUTES = {
  contractor_level: { isMultiSelect: true, defaultValue: [] },
  certifications: { isMultiSelect: true, defaultValue: [] },
  additional_languages: { isMultiSelect: true, defaultValue: [] },
  star_rating: { isMultiSelect: false, defaultValue: [0] },
};

const DEFAULT_INITIAL_STATE = {
  api: {
    contractors: [],
  },
  maxDistance: 20,
  autoExpandedRange: false,
  sortBy: 'default',
  mobileFiltersOpened: false,
};

export const findAContractorReducer = (state, action) => {
  const newState = cloneDeep(assign({}, DEFAULT_INITIAL_STATE, state));

  newState.autoExpandedRange = false;

  if (action.type === LOAD_DATA) {
    newState.api = action.data;

    if (action.data.radius !== newState.maxDistance) {
      newState.maxDistance = action.data.radius;
      newState.autoExpandedRange = true;
    }

    return newState;
  }

  if (action.type === SET_MAX_DISTANCE) {
    newState.maxDistance = action.maxDistance;
    return newState;
  }

  if (action.type === SET_SORT_BY) {
    newState.sortBy = action.sortBy;
    return newState;
  }

  if (action.type === SET_MOBILE_FILTERS_OPENED) {
    newState.mobileFiltersOpened = action.mobileFiltersOpened;
    return newState;
  }

  return newState;
};

const INVALID_ZIP_ERROR = 'zip_code_not_found';

export const updateAPI = data => ({ type: LOAD_DATA, data });
export const setMaxDistance = maxDistance => ({ type: SET_MAX_DISTANCE, maxDistance });
export const setSortBy = sortBy => ({ type: SET_SORT_BY, sortBy });
export const toggleFilter = args => (dispatch, getState) => {
  const [[filterKey, value]] = Object.entries(args);
  const ocParams = cloneDeep(getState()?.urlParams?.oc) || {};
  const filterAttribute = FILTER_ATTRIBUTES[filterKey];
  if (!filterAttribute) {
    throw new Error(`Invalid filter key: ${filterKey}`);
  }

  if (filterAttribute.isMultiSelect) {
    ocParams[filterKey] = xorWith(ocParams[filterKey], [value], isEqual);
  } else {
    ocParams[filterKey] = [value];
  }
  if (isEqual(ocParams[filterKey], filterAttribute.defaultValue)) {
    delete ocParams[filterKey];
  }
  if (isEmpty(ocParams)) {
    dispatch({ type: SET_URL_PARAM, key: 'oc', value: null });
    return;
  }

  dispatch({ type: SET_URL_PARAM, key: 'oc', value: ocParams });
};
export const setMobileFiltersOpened = mobileFiltersOpened => ({
  type: SET_MOBILE_FILTERS_OPENED,
  mobileFiltersOpened,
});
export const resetFilters = () => ({
  type: SET_URL_PARAM,
  key: 'oc',
  value: null,
});

function urlParamsToFilters(urlParams) {
  const filters = {};
  const ocParams = urlParams?.oc;
  if (!ocParams) return mapValues(FILTER_ATTRIBUTES, attr => attr.defaultValue);

  Object.keys(FILTER_ATTRIBUTES).forEach(filterType => {
    const urlValue = ocParams[filterType];
    if (urlValue && Array.isArray(urlValue)) {
      filters[filterType] = urlValue;
    } else {
      filters[filterType] = FILTER_ATTRIBUTES[filterType].defaultValue;
    }
  });
  return filters;
}

export const FindAContractorConnector = connect(
  ({ findAContractor, urlParams }) => {
    const filters = urlParamsToFilters(urlParams);
    const contractors = get(findAContractor, 'api.contractors', []);
    const filteredContractors = filters ? filterContractors(contractors, filters) : contractors;
    return {
      city: get(findAContractor, 'api.city'),
      contractors,
      filteredContractors,
      filteredContractorsCount: filteredContractors.length,
      filters,
      invalidZip: get(findAContractor, 'api.meta.errors', []).includes(INVALID_ZIP_ERROR),
      maxDistance: findAContractor.maxDistance,
      autoExpandedRange: findAContractor.autoExpandedRange,
      region: get(findAContractor, 'api.region'),
      sortBy: findAContractor.sortBy,
      totalCount: contractors.length,
      newSort: findAContractor.newSort,
      mobileFiltersOpened: findAContractor.mobileFiltersOpened,
    };
  },
  { updateAPI, setMaxDistance, setSortBy, toggleFilter, resetFilters, setMobileFiltersOpened }
);
